import React, { useEffect } from 'react';
import { Appointment, Service, Venue, AddOn } from '@/types';
import { CreditCard, FileText } from 'lucide-react';
import { Badge } from '@shared/components/ui/Badge';
import { StatusBadge } from '@shared/components/common/StatusBadge';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@shared/components/ui/Table';
import { Pagination } from '@shared/components/common/Pagination';
import { useAppointments, type AppointmentListParams } from '@features/admin/data';
import { getAppointmentServiceNames } from '@/lib/appointmentServices';

interface ReminderMeta {
  label: string;
  variant: 'success' | 'danger' | 'warning' | 'neutral';
}

export interface FinanceListProps {
  params: AppointmentListParams;
  statusFilter: string;
  servicesById: Map<string, Service>;
  venuesById: Map<string, Venue>;
  addOnsById: Map<string, AddOn>;
  calculateBookingCost: (app: Appointment) => number;
  getReminderMeta: (status?: Appointment['reminderStatus']) => ReminderMeta;
  getPaymentStatusMeta: (
    method: Appointment['paymentMethod'],
    status?: string
  ) => { label: string; variant: ReminderMeta['variant'] | 'info' };
  formatPaymentRef: (app: Appointment) => string;
  formatDate: (value?: string | Date | null) => string;
  formatDateTime: (value?: string | Date | null) => string;
  hasInvoicePaymentAnomaly: (app: Appointment) => boolean;
  toggleDateSort: () => void;
  renderDateSortIcon: () => React.ReactNode;
  onOpenPaymentModal: (app: Appointment) => void;
  onItemsLoaded: (items: Appointment[]) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

export const FinanceList: React.FC<FinanceListProps> = ({
  params,
  statusFilter,
  servicesById,
  venuesById,
  addOnsById,
  calculateBookingCost,
  getReminderMeta,
  getPaymentStatusMeta,
  formatPaymentRef,
  formatDate,
  formatDateTime,
  hasInvoicePaymentAnomaly,
  toggleDateSort,
  renderDateSortIcon,
  onOpenPaymentModal,
  onItemsLoaded,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange
}) => {
  const result = useAppointments(params);
  const fetchedAppointments = result.items || [];
  const totalCount = Number(result.totalCount || 0);

  useEffect(() => {
    onItemsLoaded(fetchedAppointments);
  }, [fetchedAppointments, onItemsLoaded]);

  const displayAppointments =
    statusFilter === 'billable'
      ? fetchedAppointments.filter((app) => app.status === 'completed' || app.status === 'noshow')
      : fetchedAppointments;

  return (
    <>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="w-full text-left text-sm">
            <THead className="bg-slate-50 border-b text-slate-600">
              <TR>
                <TH>
                  <button
                    type="button"
                    onClick={toggleDateSort}
                    className="inline-flex items-center gap-1.5 hover:text-slate-800"
                    title="Toggle date sort"
                  >
                    Date
                    {renderDateSortIcon()}
                  </button>
                </TH>
                <TH>Time</TH>
                <TH>Reference</TH>
                <TH>PO Number</TH>
                <TH>Customer / Company</TH>
                <TH>Email</TH>
                <TH>Booked By</TH>
                <TH>Service</TH>
                <TH>Add-ons</TH>
                <TH>Location</TH>
                <TH>Status</TH>
                <TH>Reminder</TH>
                <TH className="min-w-[260px]">Payment</TH>
                <TH align="right">Amount</TH>
              </TR>
            </THead>
            <TBody className="divide-y divide-slate-100">
              {displayAppointments.length === 0 ? (
                <EmptyRow colSpan={14} className="p-8 text-center text-slate-500 italic">
                  No records found matching your filters.
                </EmptyRow>
              ) : (
                displayAppointments.map((app) => {
                  const serviceNames = getAppointmentServiceNames(app, servicesById);
                  const venue = venuesById.get(app.venueId);
                  const addonNames = (app.addOnIds || [])
                    .map((id) => addOnsById.get(id)?.name)
                    .filter(Boolean)
                    .join(', ');
                  const cost = calculateBookingCost(app);
                  const reminderMeta = getReminderMeta(app.reminderStatus);
                  const paymentMeta = getPaymentStatusMeta(app.paymentMethod, app.paymentStatus);
                  const paymentRef = formatPaymentRef(app);
                  const paymentProvider =
                    app.paymentMethod === 'invoice' ? '' : app.paymentProvider || '';
                  const paymentCapturedAt =
                    app.paymentMethod === 'invoice' || !app.paymentCapturedAt
                      ? ''
                      : formatDateTime(app.paymentCapturedAt);
                  const invoicePaymentAnomaly = hasInvoicePaymentAnomaly(app);

                  return (
                    <TR key={app.id} className="hover:bg-slate-50">
                      <TD className="text-slate-600">{formatDate(app.date)}</TD>
                      <TD className="text-slate-600">{app.time || '-'}</TD>
                      <TD className="font-mono text-xs">{app.id}</TD>
                      <TD className="font-medium text-slate-800">
                        {app.poNumber || (
                          <span className="text-slate-300 italic text-xs">None</span>
                        )}
                      </TD>
                      <TD className="min-w-[260px]">
                        <div className="font-medium text-slate-900">{app.customerName}</div>
                        <div className="text-xs text-slate-500">
                          {app.companyName || 'Individual'}
                        </div>
                      </TD>
                      <TD className="text-slate-600">{app.customerEmail || '-'}</TD>
                      <TD className="text-slate-600">{app.bookedByEmail || '-'}</TD>
                      <TD className="text-slate-600">{serviceNames || '—'}</TD>
                      <TD className="text-xs text-slate-500">{addonNames || 'None'}</TD>
                      <TD className="text-slate-600">{venue?.name || app.venueName || '—'}</TD>
                      <TD>
                        <StatusBadge status={app.status} />
                      </TD>
                      <TD align="center">
                        <Badge variant={reminderMeta.variant}>{reminderMeta.label}</Badge>
                      </TD>
                      <TD>
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {app.paymentMethod === 'invoice' ? (
                              <FileText className="w-3 h-3 text-blue-500" />
                            ) : (
                              <CreditCard className="w-3 h-3 text-purple-500" />
                            )}
                            <span className="capitalize">{app.paymentMethod}</span>
                            <Badge variant={paymentMeta.variant}>{paymentMeta.label}</Badge>
                            {invoicePaymentAnomaly ? (
                              <Badge variant="warning">Payment Anomaly</Badge>
                            ) : null}
                          </div>
                          <div className="text-[10px] text-slate-400">
                            {paymentProvider || '—'}
                            {paymentRef ? ` · ${paymentRef}` : ''}
                          </div>
                          {paymentMeta.label === 'Captured' && paymentCapturedAt ? (
                            <div className="text-[10px] text-slate-400">
                              Captured {paymentCapturedAt}
                            </div>
                          ) : null}
                          {paymentMeta.label === 'Failed' && app.paymentErrorMessage ? (
                            <div
                              className="text-[10px] text-red-500 truncate max-w-[220px]"
                              title={app.paymentErrorMessage}
                            >
                              {app.paymentErrorMessage}
                            </div>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => onOpenPaymentModal(app)}
                            className="text-[10px] font-bold text-teal-600 hover:text-teal-800 text-left"
                          >
                            View Payment Details
                          </button>
                        </div>
                      </TD>
                      <TD align="right" className="font-medium text-slate-900">
                        £{cost.toFixed(2)}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </div>
      <Pagination
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </>
  );
};
